import { useEffect, useState } from "preact/hooks";

// QA-B1 fix (design-phase-c.md §5.2 note) — the global Quick Add FAB
// (`QuickAdd.tsx`, mounted once in `app.tsx`) and `Snackbar` (mounted
// independently, per-screen, by `useDue.ts`/`useSchedule.ts`/every
// screen's own local toast state) have no shared parent that already
// knows about both of them — `Snackbar` is rendered from a dozen
// different call sites, several of which are siblings of the FAB rather
// than ancestors. A tiny app-wide "is an action Snackbar currently
// showing" signal is the only way for the FAB (which has no idea which
// screen is mounted, let alone whether IT has a Snackbar up) to yield
// the tap to Undo, regardless of which of those call sites raised it.
//
// Scoped to ACTION Snackbars only (`state.onUndo` present) — a plain
// message-only toast ("Saved a note for Biscuit.") has no competing tap
// target, so the FAB has nothing to intercept and no reason to move.

type Listener = (visible: boolean) => void;

const listeners = new Set<Listener>();
let activeCount = 0;

function notify(): void {
  const visible = activeCount > 0;
  for (const listener of listeners) listener(visible);
}

/**
 * Call once for the lifetime of a visible action Snackbar (e.g. from a
 * `useEffect` keyed on the Snackbar becoming visible-with-`onUndo`).
 * Returns the matching "no longer visible" unregister function — call it
 * on dismiss/unmount, exactly like an event-listener cleanup.
 */
export function registerActionSnackbarVisible(): () => void {
  activeCount += 1;
  notify();
  let released = false;
  return () => {
    if (released) return; // idempotent — double-invocation (e.g. StrictMode-style double effects) must never under-flow the counter.
    released = true;
    activeCount = Math.max(0, activeCount - 1);
    notify();
  };
}

/** `true` while at least one action Snackbar is visible anywhere in the app. */
export function useAnyActionSnackbarVisible(): boolean {
  const [visible, setVisible] = useState(() => activeCount > 0);

  useEffect(() => {
    // The count may have changed between this hook's first render and
    // this effect running — resync rather than trust the initializer.
    setVisible(activeCount > 0);
    const listener: Listener = (next) => setVisible(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return visible;
}
